import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, FileText, Loader2, X } from "lucide-react";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function Quiz({ token, lesson, onPassed }) {
  const [picks, setPicks] = useState([null, null, null]);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/coach-ready/learn/${token}/lessons/${lesson.n}/quiz`, { answers: picks });
      setRes(data);
      if (data.passed) onPassed(data);
    } finally { setBusy(false); }
  };
  const retry = () => { setRes(null); setPicks([null, null, null]); };
  return (
    <section className="mt-10 border-2 border-[#1E1E1E] bg-white p-5 sm:p-6" data-testid="cr-quiz">
      <span className="kicker" style={{ color: "#DB9130" }}>Quiz</span>
      <h2 className="font-head uppercase text-2xl text-[#1E1E1E]">Three quick questions</h2>
      <p className="mt-1 text-sm text-[#6E6E6E]">Get all three right to complete the lesson. You can retry as many times as you like.</p>
      <ol className="mt-5 space-y-6">
        {lesson.quiz.map((q, qi) => (
          <li key={q.q} data-testid={`cr-q-${qi}`}>
            <p className="font-bold text-[#1E1E1E] flex gap-2">
              <span>{qi + 1}.</span><span className="flex-1">{q.q}</span>
              {res && (res.results[qi]
                ? <span className="text-[#2F7A4B] flex items-center gap-1 text-sm" data-testid={`cr-q-${qi}-right`}><Check size={16} />Right</span>
                : <span className="text-[#B23A2E] flex items-center gap-1 text-sm" data-testid={`cr-q-${qi}-wrong`}><X size={16} />Not quite</span>)}
            </p>
            <div className="mt-2 space-y-2">
              {q.options.map((o, oi) => (
                <label key={o} className={`flex gap-3 items-start border-2 p-3 text-sm cursor-pointer ${picks[qi] === oi ? "border-[#1E1E1E] bg-[#F5F0EA]" : "border-[#D3CFC0]"}`}>
                  <input type="radio" name={`q${qi}`} checked={picks[qi] === oi} disabled={!!res}
                    onChange={() => setPicks((p) => p.map((v, i) => (i === qi ? oi : v)))} data-testid={`cr-q-${qi}-opt-${oi}`} className="mt-0.5 accent-[#E8A33D]" />
                  <span>{o}</span>
                </label>
              ))}
            </div>
          </li>
        ))}
      </ol>
      {!res && (
        <button onClick={submit} disabled={busy || picks.includes(null)} data-testid="cr-quiz-submit"
          className="mt-6 w-full bg-[#E8A33D] text-[#1A1A1A] font-head uppercase tracking-wide text-sm py-3.5 hover:bg-[#D6922C] transition-colors disabled:opacity-50">
          {busy ? "Checking…" : "Check my answers"}
        </button>
      )}
      {res && !res.passed && (
        <div className="mt-6" data-testid="cr-quiz-failed">
          <p className="text-sm text-[#1E1E1E]">You got {res.results.filter(Boolean).length} of 3. Re-read the lesson and try again.</p>
          <button onClick={retry} data-testid="cr-quiz-retry"
            className="mt-3 w-full border-2 border-[#1E1E1E] text-[#1E1E1E] font-head uppercase tracking-wide text-sm py-3 hover:bg-[#1E1E1E] hover:text-[#F5F0EA] transition-colors">Try again</button>
        </div>
      )}
      {res && res.passed && <p className="mt-6 border-2 border-[#2F7A4B] p-3 text-sm font-bold text-[#2F7A4B]" data-testid="cr-quiz-passed">All three right. Lesson complete.</p>}
    </section>
  );
}

export default function CoachReadyLesson() {
  const { token, n } = useParams();
  const [l, setL] = useState(null);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);
  useEffect(() => {
    window.scrollTo(0, 0);
    setL(null); setDone(null);
    api.get(`/coach-ready/learn/${token}/lessons/${n}`).then(({ data }) => setL(data)).catch((e) => setErr(e?.response?.data?.detail || "Couldn't load this lesson."));
  }, [token, n]);
  if (err) return <div className="max-w-md mx-auto px-4 py-20 text-center text-[#3A3A3A]" data-testid="cr-error">{err}</div>;
  if (!l) return <div className="py-24 flex justify-center"><Loader2 className="animate-spin" size={32} /></div>;
  const back = `/coach-ready/learn/${token}`;
  const completed = l.completed || done?.passed;
  return (
    <article className="max-w-2xl mx-auto px-4 sm:px-6 pt-6 pb-16" data-testid="cr-lesson">
      <Seo title={l.title} brand="VoluntoldHQ" path="/coach-ready/learn" noindex />
      <Link to={back} className="inline-flex items-center gap-1 text-xs uppercase tracking-wide font-bold text-[#3A3A3A]" data-testid="cr-back"><ArrowLeft size={14} /> All lessons</Link>
      <span className="mt-5 block kicker" style={{ color: "#DB9130" }}>{l.n === 0 ? "Start here" : `Lesson ${l.n} of ${l.total}`}{completed ? " · Complete" : ""}</span>
      <h1 className="mt-1 font-head font-semibold uppercase text-3xl sm:text-4xl text-[#1E1E1E] leading-tight" data-testid="cr-lesson-title">{l.title}</h1>
      {l.outcomes.length > 0 && (
        <ul className="mt-4 space-y-1.5 border-l-4 border-[#E8A33D] bg-[#E7E3D9] p-4 text-sm text-[#1E1E1E]" data-testid="cr-outcomes">
          {l.outcomes.map((o) => <li key={o} className="flex gap-2"><Check size={15} className="mt-0.5 text-[#DB9130] shrink-0" />{o}</li>)}
        </ul>
      )}
      {/* Lesson HTML is rendered server-side from the owner's lesson files (trusted, no user input). */}
      <div className="cr-prose mt-6" data-testid="cr-lesson-body" dangerouslySetInnerHTML={{ __html: l.html }} />
      {l.toolkit_files.length > 0 && (
        <div className="mt-8 border-2 border-[#1E1E1E] p-4" data-testid="cr-lesson-toolkit">
          <span className="kicker" style={{ color: "#DB9130" }}>From your Toolkit</span>
          <ul className="mt-2 space-y-1.5 text-sm">
            {l.toolkit_files.map((f) => <li key={f.url}><a href={`${BACKEND}${f.url}`} className="flex gap-2 font-bold text-[#1E1E1E] hover:text-[#DB9130]"><FileText size={15} className="mt-0.5 shrink-0" />{f.name}</a></li>)}
          </ul>
        </div>
      )}
      {l.quiz.length > 0 && !l.completed && <Quiz token={token} lesson={l} onPassed={setDone} />}
      {l.completed && l.quiz.length > 0 && <p className="mt-10 border-2 border-[#2F7A4B] p-3 text-sm font-bold text-[#2F7A4B]" data-testid="cr-already-complete">You&apos;ve completed this lesson.</p>}
      <div className="mt-8 flex justify-between gap-3">
        {l.prev !== null ? <Link to={`${back}/${l.prev}`} className="inline-flex items-center gap-1 text-sm font-bold text-[#1E1E1E]" data-testid="cr-prev"><ArrowLeft size={16} />Previous</Link> : <span />}
        {l.next !== null
          ? <Link to={`${back}/${l.next}`} data-testid="cr-next" className="inline-flex items-center gap-1 bg-[#1E1E1E] text-[#F5F0EA] font-head uppercase tracking-wide text-sm px-5 py-3 hover:bg-[#2D2D2D] transition-colors">
              {l.n === 0 ? "Start Lesson 1" : "Next lesson"}<ArrowRight size={16} /></Link>
          : <Link to={back} data-testid="cr-finish" className="inline-flex items-center gap-1 bg-[#1E1E1E] text-[#F5F0EA] font-head uppercase tracking-wide text-sm px-5 py-3">Back to all lessons</Link>}
      </div>
    </article>
  );
}
